import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Mail, Loader2, Save, Eye } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { TeacherShell } from "@/components/TeacherShell";
import { api, formatApiErrorDetail } from "@/lib/api";

const VARIABLE_HELP = {
  prenom: "Prénom de l'élève",
  nom: "Nom de l'élève",
  classe: "Nom de la classe",
  semaine: "Semaine concernée",
  enseignant: "Votre nom",
};

function renderPreview(text, teacherName) {
  const ctx = {
    prenom: "Marie", nom: "Dupont", classe: "CE2 A",
    semaine: "semaine du 15/09 au 19/09", enseignant: teacherName || "L'enseignant",
  };
  return Object.entries(ctx).reduce((acc, [k, v]) => acc.split(`{${k}}`).join(v), text || "");
}

export default function EmailTemplate() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const teacherName = `${user?.first_name || ""} ${user?.last_name || ""}`.trim();

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [signature, setSignature] = useState("");
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/email-template");
        setSubject(data.subject);
        setBody(data.body);
        setSignature(data.signature || "");
        setVariables(data.variables || []);
      } catch {
        toast.error("Chargement du modèle impossible.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("L'objet et le texte sont obligatoires.");
      return;
    }
    setSaving(true);
    try {
      await api.put("/email-template", { subject, body, signature });
      toast.success("Modèle d'e-mail enregistré.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <TeacherShell active="email">
      <div className="mx-auto max-w-4xl">
        <h1 className="mb-6 flex items-center gap-2 font-heading text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
          <Mail size={22} className="text-blue-700 dark:text-blue-400" /> Modèle d'e-mail
        </h1>
        {loading ? (
          <div className="flex justify-center py-20" data-testid="template-loading">
            <Loader2 className="animate-spin text-slate-400" size={26} />
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="grid grid-cols-1 gap-5 lg:grid-cols-2"
          >
            <div className="flex flex-col gap-4">
              <div className="rounded-2xl border border-slate-100 bg-white p-4 dark:border-white/10 dark:bg-white/[0.03]">
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-400">Variables disponibles</p>
                <div className="flex flex-wrap gap-2" data-testid="variable-chips">
                  {variables.map((v) => (
                    <span key={v} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600 dark:bg-white/10 dark:text-slate-300" title={VARIABLE_HELP[v]}>
                      {"{" + v + "}"}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="subject">Objet</Label>
                <Input id="subject" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="template-subject" />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="body">Texte du message</Label>
                <textarea
                  id="body" value={body} onChange={(e) => setBody(e.target.value)}
                  rows={8}
                  className="rounded-md border border-input bg-transparent px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900"
                  data-testid="template-body"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="signature">Signature</Label>
                <Input id="signature" value={signature} onChange={(e) => setSignature(e.target.value)} data-testid="template-signature" />
              </div>

              <div className="flex justify-end">
                <Button onClick={save} disabled={saving} className="rounded-xl" data-testid="save-template-button">
                  {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />}
                  Enregistrer le modèle
                </Button>
              </div>
            </div>

            <div>
              <p className="mb-2 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-400">
                <Eye size={14} /> Aperçu
              </p>
              <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)] dark:border-white/10 dark:bg-white/[0.03]" data-testid="template-preview">
                <p className="mb-4 text-base font-semibold text-slate-900 dark:text-slate-100">
                  {renderPreview(subject, teacherName)}
                </p>
                <div className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700 dark:text-slate-200">
                  {renderPreview(body, teacherName)}
                </div>
                {signature.trim() && (
                  <p className="mt-4 text-sm text-slate-500">{renderPreview(signature, teacherName)}</p>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </TeacherShell>
  );
}
